// Data hooks for the reports feature (plan.md G25). Thin wrappers over
// hooks/useApi.ts's useApiQuery (the shared primitive every web module goal
// builds on) over the reporting/search module (G19) plus the small set of
// read-only masters lookups (config/purchasing/sales) every report's filter
// bar needs — following the same "independently wrap the read-only masters
// endpoints" convention every prior web goal used (see features/silo-counts/
// api.ts and features/purchasing-intake/api.ts's own copies of useSites/
// useProducts/etc.).
import { useApiQuery } from '../../hooks/useApi';
import type {
  ConsumableSpendResponse,
  CostCentreRow,
  CustomerRow,
  DispatchRow,
  GenealogyResponse,
  LocationRow,
  MarginByDispatchRow,
  MarginByInvoiceRow,
  MarginReportResponse,
  MassBalanceResponse,
  MovementLedgerResponse,
  Page,
  ProcessStageRow,
  ProductRow,
  PurchasingReportResponse,
  ReorderSuggestionsResponse,
  SalesReportResponse,
  SiloDriftResponse,
  SiteRow,
  StockOnHandResponse,
  SupplierRow,
  UninvoicedDispatchesResponse,
  YieldReportResponse,
} from './types';

/**
 * Every date-range filter in this feature holds `YYYY-MM-DD` strings
 * (`DatePickerInput`'s `onChange` always reports this `DateStringValue` form —
 * see components/DateRangeFilter.tsx's header). `packages/core/src/schemas/
 * reporting.ts`'s `from`/`to` are inclusive business-date bounds, but a bare
 * `YYYY-MM-DD` parses (via the server's `z.coerce.date()`) as that day's
 * midnight — so a date-only `to` would silently exclude the rest of its own
 * day. This helper expands `from`/`to` to the start/end of their respective
 * days before they go out as query params, once, for every report hook below.
 *
 * BUSINESS-DAY TIMEZONE (spec §4 item 7, Amendment 2026-07-10): the plants run
 * on SAST (Africa/Johannesburg, a FIXED UTC+2 offset year-round — no DST), but
 * the deployed API Lambda has no guaranteed local timezone. An offset-LESS
 * datetime string here (`${date}T00:00:00.000`) used to be coerced as UTC on
 * whatever host parsed it — correct by accident on a developer's SAST machine,
 * silently wrong on the UTC Lambda (a transaction posted 00:00-02:00 SAST would
 * fall outside "today"'s report window). The explicit `+02:00` suffix below
 * makes the instant unambiguous regardless of server timezone — mirrors
 * `@zebra/core/src/time.ts`'s `sastDayStart`/`sastDayEnd` (duplicated here as a
 * literal offset, not value-imported, since this file only ever TYPE-imports
 * from `@zebra/core` — see features/landed-cost/api.ts's identical convention
 * for why a bare string constant isn't worth a runtime package dependency).
 */
export function rangeQuery(from?: string | null, to?: string | null): { from?: string; to?: string } {
  return {
    from: from ? `${from}T00:00:00.000+02:00` : undefined,
    to: to ? `${to}T23:59:59.999+02:00` : undefined,
  };
}

// ---------------------------------------------------------------------------
// Read-only masters lookups — generous single-page fetches (v1 tenants have
// small masters, in the tens not thousands; matches every sibling feature's own
// convention rather than building typeahead search for these pickers).
// ---------------------------------------------------------------------------

export function useSites() {
  return useApiQuery<Page<SiteRow>>(['config', 'sites'], '/config/sites', { query: { limit: 200 } });
}

export function useLocations(params: { siteId?: string } = {}) {
  return useApiQuery<Page<LocationRow>>(['config', 'locations', params], '/config/locations', {
    query: { siteId: params.siteId, limit: 200 },
  });
}

export function useProducts(params: { isActive?: boolean } = {}) {
  return useApiQuery<Page<ProductRow>>(['config', 'products', params], '/config/products', {
    query: { isActive: params.isActive, limit: 200 },
  });
}

export function useCostCentres(params: { siteId?: string } = {}) {
  return useApiQuery<Page<CostCentreRow>>(['config', 'cost-centres', params], '/config/cost-centres', {
    query: { siteId: params.siteId, limit: 200 },
  });
}

export function useProcessStages(params: { siteId?: string } = {}) {
  return useApiQuery<{ items: ProcessStageRow[] }>(['config', 'process-stages', params], '/config/process-stages', {
    query: { siteId: params.siteId },
  });
}

export function useSuppliers(params: { isActive?: boolean } = {}) {
  return useApiQuery<Page<SupplierRow>>(['purchasing', 'suppliers', params], '/purchasing/suppliers', {
    query: { isActive: params.isActive, limit: 200 },
  });
}

export function useCustomers(params: { isActive?: boolean } = {}) {
  return useApiQuery<Page<CustomerRow>>(['sales', 'customers', params], '/sales/customers', {
    query: { isActive: params.isActive, limit: 200 },
  });
}

/** POSTED dispatches only — the genealogy lookup's dispatch picker (§9.7 only
 * makes sense once a dispatch has actually posted DISPATCH movements). */
export function useDispatchesForGenealogy() {
  return useApiQuery<Page<DispatchRow>>(['sales', 'dispatches', 'posted-for-genealogy'], '/sales/dispatches', {
    query: { status: 'POSTED', limit: 200 },
  });
}

// ---------------------------------------------------------------------------
// 1. Stock on hand & valuation
// ---------------------------------------------------------------------------
export interface StockOnHandParams {
  siteId?: string;
  locationId?: string;
  productId?: string;
}

export function useStockOnHandReport(params: StockOnHandParams) {
  return useApiQuery<StockOnHandResponse>(['reporting', 'stock-on-hand', params], '/reporting/stock-on-hand', {
    query: { siteId: params.siteId, locationId: params.locationId, productId: params.productId },
  });
}

// ---------------------------------------------------------------------------
// 2. Movement ledger browser
// ---------------------------------------------------------------------------
export interface MovementLedgerParams {
  siteId?: string;
  locationId?: string;
  productId?: string;
  type?: string;
  from?: string | null;
  to?: string | null;
  cursor?: string;
  limit?: number;
}

export function useMovementLedgerReport(params: MovementLedgerParams) {
  return useApiQuery<MovementLedgerResponse>(['reporting', 'movements', params], '/reporting/movements', {
    query: {
      siteId: params.siteId,
      locationId: params.locationId,
      productId: params.productId,
      type: params.type,
      ...rangeQuery(params.from, params.to),
      cursor: params.cursor,
      limit: params.limit ?? 100,
    },
  });
}

// ---------------------------------------------------------------------------
// 3. Mass balance — requires siteId + from + to (server-side, no defaults)
// ---------------------------------------------------------------------------
export interface MassBalanceParams {
  siteId: string | null;
  from: string | null;
  to: string | null;
}

export function useMassBalanceReport(params: MassBalanceParams) {
  const enabled = Boolean(params.siteId && params.from && params.to);
  return useApiQuery<MassBalanceResponse>(['reporting', 'mass-balance', params], '/reporting/mass-balance', {
    query: {
      siteId: params.siteId ?? undefined,
      ...rangeQuery(params.from, params.to),
    },
    enabled,
  });
}

// ---------------------------------------------------------------------------
// 4. Yield by stage/run
// ---------------------------------------------------------------------------
export interface YieldReportParams {
  siteId?: string;
  stageId?: string;
  from?: string | null;
  to?: string | null;
}

export function useYieldReport(params: YieldReportParams) {
  return useApiQuery<YieldReportResponse>(['reporting', 'yield', params], '/reporting/yield', {
    query: {
      siteId: params.siteId,
      stageId: params.stageId,
      ...rangeQuery(params.from, params.to),
    },
  });
}

// ---------------------------------------------------------------------------
// 5. Silo drift history
// ---------------------------------------------------------------------------
export interface SiloDriftParams {
  siteId?: string;
  locationId?: string;
  productId?: string;
  from?: string | null;
  to?: string | null;
}

export function useSiloDriftReport(params: SiloDriftParams) {
  return useApiQuery<SiloDriftResponse>(['reporting', 'silo-drift', params], '/reporting/silo-drift', {
    query: {
      siteId: params.siteId,
      locationId: params.locationId,
      productId: params.productId,
      ...rangeQuery(params.from, params.to),
    },
  });
}

// ---------------------------------------------------------------------------
// 6. Open/partial PO book + supplier spend
// ---------------------------------------------------------------------------
export interface PurchasingReportParams {
  siteId?: string;
  supplierId?: string;
  from?: string | null;
  to?: string | null;
}

export function usePurchasingReport(params: PurchasingReportParams) {
  return useApiQuery<PurchasingReportResponse>(['reporting', 'purchasing', params], '/reporting/purchasing', {
    query: {
      siteId: params.siteId,
      supplierId: params.supplierId,
      ...rangeQuery(params.from, params.to),
    },
  });
}

// ---------------------------------------------------------------------------
// 7. Sales by customer/product/period
// ---------------------------------------------------------------------------
export interface SalesReportParams {
  siteId?: string;
  customerId?: string;
  productId?: string;
  from?: string | null;
  to?: string | null;
}

export function useSalesReport(params: SalesReportParams) {
  return useApiQuery<SalesReportResponse>(['reporting', 'sales', params], '/reporting/sales', {
    query: {
      siteId: params.siteId,
      customerId: params.customerId,
      productId: params.productId,
      ...rangeQuery(params.from, params.to),
    },
  });
}

// ---------------------------------------------------------------------------
// 8. Uninvoiced dispatches
// ---------------------------------------------------------------------------
export interface UninvoicedDispatchesParams {
  siteId?: string;
  customerId?: string;
}

export function useUninvoicedDispatchesReport(params: UninvoicedDispatchesParams) {
  return useApiQuery<UninvoicedDispatchesResponse>(
    ['reporting', 'uninvoiced-dispatches', params],
    '/reporting/uninvoiced-dispatches',
    { query: { siteId: params.siteId, customerId: params.customerId } },
  );
}

// ---------------------------------------------------------------------------
// 9. Consumable spend by cost centre
// ---------------------------------------------------------------------------
export interface ConsumableSpendParams {
  siteId?: string;
  costCentreId?: string;
  from?: string | null;
  to?: string | null;
}

export function useConsumableSpendReport(params: ConsumableSpendParams) {
  return useApiQuery<ConsumableSpendResponse>(['reporting', 'consumable-spend', params], '/reporting/consumable-spend', {
    query: {
      siteId: params.siteId,
      costCentreId: params.costCentreId,
      ...rangeQuery(params.from, params.to),
    },
  });
}

// ---------------------------------------------------------------------------
// 10. Reorder suggestions
// ---------------------------------------------------------------------------
export interface ReorderSuggestionsParams {
  siteId?: string;
}

export function useReorderSuggestionsReport(params: ReorderSuggestionsParams) {
  return useApiQuery<ReorderSuggestionsResponse>(['reporting', 'reorder', params], '/reporting/reorder', {
    query: { siteId: params.siteId },
  });
}

// ---------------------------------------------------------------------------
// 11. Margin per dispatch/invoice
// ---------------------------------------------------------------------------
export interface MarginReportParams {
  siteId?: string;
  customerId?: string;
  productId?: string;
  from?: string | null;
  to?: string | null;
  groupBy: 'dispatch' | 'invoice';
}

export function useMarginReport(params: MarginReportParams) {
  return useApiQuery<MarginReportResponse<MarginByDispatchRow | MarginByInvoiceRow>>(
    ['reporting', 'margin', params],
    '/reporting/margin',
    {
      query: {
        siteId: params.siteId,
        customerId: params.customerId,
        productId: params.productId,
        ...rangeQuery(params.from, params.to),
        groupBy: params.groupBy,
      },
    },
  );
}

// ---------------------------------------------------------------------------
// 12. Time-windowed genealogy — a lookup form by dispatch (goal text)
// ---------------------------------------------------------------------------
export function useGenealogyReport(dispatchId: string | undefined) {
  return useApiQuery<GenealogyResponse>(['reporting', 'genealogy', dispatchId], '/reporting/genealogy', {
    query: { dispatchId },
    enabled: Boolean(dispatchId),
  });
}
